import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Search, Plus } from 'lucide-react';

interface Item {
  label: string;
  id?: string;
}

interface Category {
  category: string;
  items: Item[] | string[];
}

interface SearchableDropdownProps {
  inputValue: string;
  setInputValue: (value: string) => void;
  onSelect: (value: string) => void;
  categories: Category[];
  placeholder?: string;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (value: boolean) => void;
  filterItems?: (items: (Item | string)[], searchValue: string) => (Item | string)[];
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  inputValue,
  setInputValue,
  onSelect,
  categories,
  placeholder = "Search...",
  isDropdownOpen,
  setIsDropdownOpen,
  filterItems = (items, searchValue) => 
    items.filter(item => 
      (typeof item === 'string' ? 
        item.toLowerCase().includes(searchValue.toLowerCase()) :
        item.label.toLowerCase().includes(searchValue.toLowerCase())
      )
    )
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Reset focused index when input value changes or dropdown opens
  useEffect(() => {
    setFocusedIndex(0);
  }, [inputValue, isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current && 
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setFocusedIndex(0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsDropdownOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen) {
      if (e.key === 'Enter' && inputValue.trim()) {
        onSelect(inputValue);
        setIsDropdownOpen(false);
      }
      return;
    }

    const allItems = filteredCategories.flatMap(category => 
      category.items.map(item => ({
        value: typeof item === 'string' ? item : item.label,
        category: category.category
      }))
    );

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < allItems.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : allItems.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        // Handle Enter key for both matched items and "Add" option
        if (allItems.length > 0 && allItems[focusedIndex]) {
          onSelect(allItems[focusedIndex].value);
          setIsDropdownOpen(false);
          setFocusedIndex(0);
        } else if (inputValue.trim()) {
          // If there are no matches and we have input, add the custom value
          onSelect(inputValue);
          setIsDropdownOpen(false);
          setFocusedIndex(0);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setFocusedIndex(0);
        break;
    }
  };

  const filteredCategories = categories
    .map(category => ({
      ...category,
      items: filterItems(category.items, inputValue)
    }))
    .filter(category => category.items.length > 0);

  // Get all items for focus management
  const allItems = filteredCategories.flatMap(category => 
    category.items.map(item => ({
      value: typeof item === 'string' ? item : item.label,
      category: category.category
    }))
  );

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsDropdownOpen(true);
          }}
          onFocus={() => setIsDropdownOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full pr-10"
        />
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
      </div>

      {isDropdownOpen && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full max-h-[400px] overflow-y-auto bg-background/70 backdrop-blur-sm rounded-lg shadow-lg border divide-y divide-border"
        >
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <div key={category.category} className="p-1">
                <div className="px-3 py-1.5 text-sm font-medium text-foreground">
                  {category.category}
                </div>
                <div className="space-y-0.5">
                  {category.items.map((item, index) => {
                    const itemValue = typeof item === 'string' ? item : item.label;
                    const isFocused = allItems.findIndex(i => i.value === itemValue) === focusedIndex;
                    
                    return (
                      <div
                        key={typeof item === 'string' ? item : item.id || item.label}
                        className={`px-3 py-1.5 cursor-pointer text-sm transition-colors duration-150
                          ${isFocused ? 'bg-primary/10 text-primary' : 'hover:bg-muted/80'}
                          ${isFocused ? 'ring-2 ring-primary/20' : ''}`}
                        onClick={() => {
                          onSelect(itemValue);
                          setIsDropdownOpen(false);
                          setFocusedIndex(0);
                        }}
                        onMouseEnter={() => setFocusedIndex(allItems.findIndex(i => i.value === itemValue))}
                      >
                        {itemValue}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : inputValue.trim() ? (
            <div 
              className={`p-3 cursor-pointer flex items-center gap-2 transition-colors duration-150
                ${focusedIndex === 0 ? 'bg-primary/10 text-primary ring-2 ring-primary/20' : 'hover:bg-muted/80'}`}
              onClick={() => {
                onSelect(inputValue);
                setIsDropdownOpen(false);
                setFocusedIndex(0);
              }}
              onMouseEnter={() => setFocusedIndex(0)}
            >
              <Plus className="h-4 w-4 text-muted-foreground/60" />
              <span className="text-sm">Add "{inputValue}"</span>
            </div>
          ) : (
            <div className="p-3 text-sm text-muted-foreground">
              Type to filter or add a custom item
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown; 